import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import './ScalpingTrading.css';

const API_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) ||
  (process.env.REACT_APP_API_URL
    ? `${String(process.env.REACT_APP_API_URL).replace(/\/$/, '')}/api`
    : 'https://trading.omniscalping.com/api');

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('sessionToken')}`,
});

const fmt = (n, d = 2) =>
  n == null || Number.isNaN(Number(n)) ? '—' : Number(n).toLocaleString(undefined, { maximumFractionDigits: d });

const fmtBps = (n) => (n == null ? '—' : `${Number(n) >= 0 ? '+' : ''}${Number(n).toFixed(1)} bps`);

function tickAgeSec(lastTickAt) {
  if (!lastTickAt) return Infinity;
  const raw = String(lastTickAt);
  const parsed = Date.parse(raw.includes('T') ? raw : `${raw.replace(' ', 'T')}Z`);
  return Number.isFinite(parsed) ? (Date.now() - parsed) / 1000 : Infinity;
}

function showFeedError(g) {
  if (!g?.lastError) return false;
  const ws = (g.wsStatus || '').toLowerCase();
  if (ws === 'error') return true;
  if (!g.enabled || ws === 'idle') return false;
  return tickAgeSec(g.lastTickAt) > 120;
}

function StatusPill({ status }) {
  const s = (status || 'unknown').toLowerCase();
  const warnStates = ['connecting', 'idle', 'degraded', 'reconnecting'];
  const cls = s === 'open' ? 'ok' : warnStates.includes(s) ? 'warn' : 'bad';
  const labels = {
    open: 'Live feed connected',
    idle: 'Idle (engine off)',
    degraded: 'Connected (some symbols failed)',
    reconnecting: 'Reconnecting…',
    connecting: 'Connecting…',
  };
  return <span className={`scalp-pill ${cls}`}>{labels[s] || s}</span>;
}

export default function ScalpingTrading() {
  const [status, setStatus] = useState(null);
  const [paper, setPaper] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ botEnabled: false, paperMode: true, notionalUsd: 2000, leverage: 2, reserveUsd: 0 });

  const load = useCallback(async () => {
    try {
      const [statusRes, paperRes] = await Promise.all([
        axios.get(`${API_BASE}/scalping/status`, { headers: authHeaders() }),
        axios.get(`${API_BASE}/scalping/paper/summary`, { headers: authHeaders() }).catch(() => ({ data: null })),
      ]);
      const data = statusRes.data;
      setStatus(data);
      setPaper(paperRes.data);
      setForm((f) => ({
        ...f,
        botEnabled: data.user.botEnabled,
        paperMode: data.user.paperMode,
        notionalUsd: data.user.notionalUsd,
        leverage: data.user.leverage,
        reserveUsd: data.user.reserveUsd,
      }));
      setError(null);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [load]);

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API_BASE}/scalping/user/settings`, patch, { headers: authHeaders() });
      setForm((f) => ({ ...f, ...data.user }));
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="scalp-page"><div className="scalp-loading">Loading scalping engine…</div></div>;

  const g = status?.global || {};
  const rec = status?.recording || {};
  const signals = status?.signals || [];
  const paperRun = paper?.run || null;
  const paperBySymbol = paper?.bySymbol || [];

  return (
    <div className="scalp-page">
      <div className="scalp-header">
        <div>
          <Link to="/dashboard" className="scalp-back">← Dashboard</Link>
          <h1>⚡ Scalping — oBot</h1>
          <p className="scalp-sub">Oracle-Anchored Adaptive Scalper · automated, 24/7, paper validation phase</p>
        </div>
        <div className="scalp-status-block">
          <StatusPill status={g.wsStatus} />
          <span className="scalp-mode">{g.paperMode ? 'PAPER MODE' : 'LIVE MODE'}</span>
        </div>
      </div>

      {error && <div className="scalp-error">{error}</div>}

      <div className="scalp-banner">
        <strong>Oracle-Anchored Adaptive Scalper.</strong> <strong>SOL-only</strong> trading (BTC/ETH/HIP-3 sidelined — still recording).
        Goal: net-positive bps on SOL, then re-enable symbols one at a time.
      </div>

      <div className="scalp-grid">
        <section className="scalp-card">
          <h2>Your oBot</h2>
          <div className="scalp-row">
            <label>Bot enabled</label>
            <button
              className={`scalp-toggle ${form.botEnabled ? 'on' : ''}`}
              disabled={saving}
              onClick={() => save({ botEnabled: !form.botEnabled })}
            >
              {form.botEnabled ? 'ON' : 'OFF'}
            </button>
          </div>
          <div className="scalp-row">
            <label>Mode</label>
            <span className="scalp-static">{form.paperMode ? 'Paper' : 'Live'} (set by engine: {g.paperMode ? 'paper' : 'live'})</span>
          </div>
          <div className="scalp-row">
            <label>Notional / trade (USD)</label>
            <input
              type="number"
              value={form.notionalUsd}
              min={100}
              onChange={(e) => setForm({ ...form, notionalUsd: e.target.value })}
              onBlur={() => save({ notionalUsd: Number(form.notionalUsd) })}
            />
          </div>
          <div className="scalp-row">
            <label>Leverage (max {fmt(g.maxLeverage, 0)}x)</label>
            <input
              type="number"
              value={form.leverage}
              min={1}
              max={g.maxLeverage || 3}
              step={0.5}
              onChange={(e) => setForm({ ...form, leverage: e.target.value })}
              onBlur={() => save({ leverage: Number(form.leverage) })}
            />
          </div>
          <div className="scalp-row">
            <label>Untouchable reserve (USD)</label>
            <input
              type="number"
              value={form.reserveUsd}
              min={0}
              onChange={(e) => setForm({ ...form, reserveUsd: e.target.value })}
              onBlur={() => save({ reserveUsd: Number(form.reserveUsd) })}
            />
          </div>
          <p className="scalp-hint">Maker-first execution keeps round-trip fees low; leverage is capped to protect against fee drag.</p>
        </section>

        <section className="scalp-card">
          <h2>Engine</h2>
          <div className="scalp-kv"><span>Feed status</span><strong><StatusPill status={g.wsStatus} /></strong></div>
          {showFeedError(g) && <div className="scalp-kv"><span>Feed error</span><strong className="scalp-errtext">{g.lastError}</strong></div>}
          <div className="scalp-kv"><span>Last tick</span><strong>{g.lastTickAt ? new Date(g.lastTickAt).toLocaleTimeString() : '—'}</strong></div>
          <div className="scalp-kv"><span>Recording</span><strong>{g.recordMarketData ? 'on' : 'off'}</strong></div>
          <div className="scalp-kv"><span>Snapshots recorded</span><strong>{fmt(rec.snapshots, 0)}</strong></div>
          <div className="scalp-kv"><span>Trade prints recorded</span><strong>{fmt(rec.trades, 0)}</strong></div>
          <div className="scalp-kv"><span>Recording since</span><strong>{rec.sinceTs ? new Date(rec.sinceTs).toLocaleString() : '—'}</strong></div>
          <div className="scalp-kv"><span>Maker only</span><strong>{g.makerOnly ? 'yes' : 'no'}</strong></div>
          <div className="scalp-kv"><span>Commission</span><strong>{fmt(g.commissionBpsPerSide, 2)} bps/side</strong></div>
        </section>
      </div>

      {paperRun && paperRun.numTrades > 0 && (
        <section className="scalp-card scalp-track">
          <h2>Paper track record (net to trader)</h2>
          <p className="scalp-hint">
            Forward, hindsight-free paper results from the live engine — net of all exchange fees, the platform
            commission and profit share, at {fmt(paperRun.notionalUsd, 0)} USD notional per trade.
          </p>
          <div className="scalp-track-cards">
            <div className="scalp-tc"><span>Trades</span><strong>{fmt(paperRun.numTrades, 0)}</strong></div>
            <div className="scalp-tc"><span>Win rate</span><strong>{paperRun.winRate == null ? '—' : `${(paperRun.winRate * 100).toFixed(0)}%`}</strong></div>
            <div className={`scalp-tc ${paperRun.netUsd >= 0 ? 'pos' : 'neg'}`}>
              <span>Net (trader)</span>
              <strong>{paperRun.netUsd < 0 ? '-' : ''}${fmt(Math.abs(paperRun.netUsd || 0), 2)}</strong>
            </div>
            {paperRun.omniRevenueUsd != null && (
              <div className="scalp-tc">
                <span>Omni revenue</span>
                <strong>${fmt(paperRun.omniRevenueUsd, 2)}</strong>
              </div>
            )}
            <div className="scalp-tc"><span>Avg net / trade</span><strong>{fmtBps(paperRun.avgNetBpsPerTrade)}</strong></div>
          </div>
          {paperBySymbol.length > 0 && (
            <table className="scalp-table">
              <thead><tr><th>Symbol</th><th>Trades</th><th>Wins</th><th>Net USD</th><th>Avg net</th></tr></thead>
              <tbody>
                {paperBySymbol.map((s) => (
                  <tr key={s.symbol}>
                    <td><strong>{s.symbol}</strong></td>
                    <td>{fmt(s.numTrades, 0)}</td>
                    <td>{fmt(s.numWins, 0)}</td>
                    <td className={s.netUsd >= 0 ? 'pos' : 'neg'}>{s.netUsd < 0 ? '-' : ''}${fmt(Math.abs(s.netUsd || 0), 2)}</td>
                    <td>{fmtBps(s.avgNetBps)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}

      <section className="scalp-card scalp-signals">
        <h2>Live oracle-deviation signal</h2>
        <table className="scalp-table">
          <thead>
            <tr>
              <th>Symbol</th>
              <th>Profile</th>
              <th>Mid</th>
              <th>Oracle</th>
              <th>Deviation</th>
              <th>Spread</th>
              <th>Imbalance</th>
              <th>Bias</th>
              <th>Tradeable</th>
            </tr>
          </thead>
          <tbody>
            {signals.map((s) => (
              <tr key={s.symbol} className={!s.hasData ? 'scalp-nodata' : ''}>
                <td><strong>{s.symbol}</strong> <span className="scalp-cat">{s.category}</span></td>
                <td><span className={`scalp-cat ${s.profileClass || ''}`}>{s.profileClass || '—'}{s.sessionMode && s.sessionMode !== 'active' ? ` · ${s.sessionMode}` : ''}</span></td>
                <td>{fmt(s.mid, 4)}</td>
                <td>{fmt(s.oraclePx, 4)}</td>
                <td className={s.devBps > 0 ? 'pos' : s.devBps < 0 ? 'neg' : ''}>{fmtBps(s.devBps)}</td>
                <td>{s.spreadBps == null ? '—' : `${fmt(s.spreadBps, 1)} bps`}</td>
                <td>{s.imbalance == null ? '—' : fmt(s.imbalance, 2)}</td>
                <td>{s.side ? <span className={`scalp-side ${s.side}`}>{s.side}</span> : '—'}</td>
                <td>{s.tradeable ? <span className="scalp-yes">●</span> : <span className="scalp-no">○</span>}</td>
              </tr>
            ))}
            {!signals.length && (
              <tr><td colSpan={9} className="scalp-empty">Waiting for market data…</td></tr>
            )}
          </tbody>
        </table>
        <p className="scalp-hint">
          Deviation = (mid − oracle) / oracle. A premium (positive) biases short, a discount (negative) biases long.
          "Tradeable" reflects the liquidity + edge gates; it does not place orders during the measurement phase.
        </p>
      </section>
    </div>
  );
}
